import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox } from '@/components/atoms/checkbox';
import { cn } from '@/lib/utils';

interface ColumnVisibilityItem {
  key: string;
  label: string;
}

interface SortableColumnVisibilityRowProps<T extends ColumnVisibilityItem> {
  column: T;
  visible: boolean;
  onToggle: () => void;
}

export function SortableColumnVisibilityRow<T extends ColumnVisibilityItem>({
  column,
  visible,
  onToggle,
}: SortableColumnVisibilityRowProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.key,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'cursor-pointer rounded-md px-2 py-1.5 hover:bg-muted/50',
        'flex items-center gap-2',
        isDragging && 'z-50 bg-muted/50 opacity-50 shadow-md',
      )}
      onClick={onToggle}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${column.label}`}
        onClick={(e) => e.stopPropagation()}
        className="touch-none cursor-grab text-muted-foreground hover:text-foreground"
      >
        <GripVertical size={13} />
      </button>
      <Checkbox
        checked={visible}
        aria-label={`${visible ? 'Hide' : 'Show'} ${column.label}`}
        onCheckedChange={onToggle}
        onClick={(e) => e.stopPropagation()}
      />
      <span className={cn('flex-1 text-xs', !visible && 'text-muted-foreground')}>
        {column.label}
      </span>
    </div>
  );
}
